import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { ATTORNEY_STEPS, ATTORNEY_QUOTES, STATS } from "../data/content";
import { ShieldCheck, Scale, Users, ArrowRight, CheckCircle2, FileCheck, Zap } from "lucide-react";
import { GoogleReviewBadge } from "../components/GoogleReviewBadge";

export default function ForAttorneys() {
  useSEO(
    // GSC-optimized: currently pos 1.64 with 0% CTR. Signals feature specificity
    // (verification + portal) that generic "For attorneys" doesn't convey.
    "Pre-Settlement Funding for Plaintiff Law Firms | Attorney Portal",
    "Attorney-founded pre-settlement funding for plaintiff law firms. Verify client cases in one click via our attorney portal. Payoff automation, firm reporting, no paralegal friction.",
    { canonical: "https://instabridgefunding.com/for-attorneys" }
  );

  return (
    <div className="min-h-screen bg-white" data-testid="attorneys-page">
      <Navbar />

      <section className="relative bg-[#0A2540] text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-[#0066FF]/20 blur-3xl" />
        <div className="absolute -left-32 bottom-0 w-96 h-96 rounded-full bg-[#0066FF]/10 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="uppercase-label !text-white/60 mb-5">For attorneys & law firms</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
            Client funding that <span className="font-semibold">protects the case and your relationship.</span>
          </h1>
          <p className="mt-7 text-lg text-white/80 leading-relaxed max-w-3xl">
            Built and run by a practicing attorney. Settlement-first underwriting. Capped advances that protect your case. Zero friction for your paralegal.
          </p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/contact"><Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-12 px-6" data-testid="atty-hero-contact">Partner With Instabridge</Button></Link>
            <Link to="/about"><Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0A2540] rounded-full h-12 px-6">Meet the founder</Button></Link>
          </div>
          <div className="mt-7"><GoogleReviewBadge variant="dark" /></div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={i}>
              <div className="font-display text-4xl lg:text-5xl font-semibold text-[#0A2540] tabular-nums">{s.v}</div>
              <div className="text-xs uppercase tracking-widest text-[#6B7280] mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why switch */}
      <section className="py-24 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">Why attorneys switch</div>
          <h2 className="font-display text-4xl lg:text-5xl text-[#0A2540] font-light tracking-tight max-w-3xl">
            Most funding companies were built by finance people who learned legal terminology.
          </h2>
          <p className="mt-6 text-lg text-[#6B7280] max-w-3xl leading-relaxed">
            Instabridge was built by an attorney who already lived it. That difference shows up in every interaction.
          </p>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <Pillar icon={Scale} t="Settlement-first underwriting" d="We cap advances to protect the recovery. Your client gets what they need without eating the settlement. We've turned down cases that would have hurt our own profit. That's the policy." />
            <Pillar icon={ShieldCheck} t="Attorney-founded" d="Not endorsed by a lawyer. Not adjacent to one. Founded and operated by a practicing attorney who has lived the contingency-fee model for over a decade." />
            <Pillar icon={Zap} t="Zero friction for your paralegal" d="Partner with Instabridge. We handle intake, underwriting, attorney verification, and communication. Your paralegal sends one form and never thinks about it again." />
          </div>
        </div>
      </section>

      {/* Process for attorneys */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="uppercase-label mb-5">The process</div>
            <h2 className="font-display text-4xl lg:text-5xl text-[#0A2540] font-light tracking-tight leading-tight">
              Three steps. <span className="font-semibold">Then it's off your plate.</span>
            </h2>
            <p className="mt-6 text-[#6B7280] leading-relaxed">
              Most attorneys spend less than 5 minutes per case from request to disbursement.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-5">
            {ATTORNEY_STEPS.map((s) => (
              <div key={s.n} className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-7">
                <div className="flex items-start gap-6">
                  <div className="font-display text-3xl font-light text-[#0066FF] tabular-nums">{s.n}</div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-[#0A2540]">{s.t}</h3>
                    <p className="text-[#6B7280] mt-2 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quotes — hidden when no verified attorney endorsements are on file.
          Prior sample quotes were removed 2026-02-17 after a provenance audit
          confirmed they were placeholder/demo names, not real attorneys. */}
      {ATTORNEY_QUOTES.length > 0 && (
      <section className="py-24 bg-[#0A2540] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="uppercase-label !text-white/60 mb-5">Attorneys who trust us</div>
          <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tight">What firms say.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {ATTORNEY_QUOTES.map((q, i) => (
              <div key={i} className="border-l-2 border-[#0066FF] pl-6 py-3">
                <p className="text-white leading-relaxed font-display font-light italic text-lg">&quot;{q.quote}&quot;</p>
                <div className="mt-5 text-sm">
                  <div className="font-semibold">{q.attorney}</div>
                  <div className="text-white/60">{q.firm}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Beyond plaintiff funding */}
      <section className="py-24 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">Beyond plaintiff funding</div>
          <h2 className="font-display text-4xl text-[#0A2540] font-light tracking-tight">Capital products for your firm.</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            <Service t="Attorney lending" d="Working-capital lines built around contingency-fee revenue and irregular settlement timing. Fund payroll, marketing, or growth." />
            <Service t="Litigation finance" d="Case-cost financing for high-value litigation. Experts, depositions, trial prep — structured around the case timeline." />
            <Service t="Medical funding" d="Letters of protection and medical-lien funding so your client gets necessary treatment while the case progresses." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-semibold">Partner With Instabridge.</h2>
          <p className="mt-3 text-[#6B7280]">Or sign in to your firm's attorney portal to manage existing cases.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/contact"><Button className="btn-primary-navy rounded-full h-12 px-7">Partner With Instabridge <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/attorney-login"><Button variant="outline" className="rounded-full h-12 px-7">Attorney portal sign in</Button></Link>
            <Link to="/about"><Button variant="ghost" className="rounded-full h-12 px-7">Meet Adam</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Pillar = ({ icon: Icon, t, d }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl p-7 hover:-translate-y-0.5 transition-transform">
    <Icon className="w-6 h-6 text-[#0066FF]" />
    <h3 className="font-display text-xl font-semibold text-[#0A2540] mt-4">{t}</h3>
    <p className="text-[#6B7280] mt-3 leading-relaxed">{d}</p>
  </div>
);

const Service = ({ t, d }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl p-6">
    <FileCheck className="w-5 h-5 text-[#0066FF]" />
    <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">{t}</h3>
    <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{d}</p>
  </div>
);
